import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Spinner, Card, Badge } from "react-bootstrap";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import Dashboardheader from "./dashboardheader";
import Sidebar from "./sidebar";
import toast from "react-hot-toast";
import { 
  BsArrowLeftCircleFill, 
  BsCalendar2Week, 
  BsClock, 
  BsPlus, 
  BsTrash, 
  BsCheckCircle,
  BsPersonBadge,
  BsSave
} from "react-icons/bs";
import { 
  FaCalendarAlt, 
  FaClock, 
  FaUserClock, 
  FaPlus, 
  FaTrash,
  FaCheck,
  FaUser
} from "react-icons/fa";
import { 
  MdSchedule, 
  MdAccessTime, 
  MdAdd, 
  MdDelete,
  MdSave,
  MdPerson
} from "react-icons/md";
import config from "../../config/config";
import { Link } from "react-router-dom";
import { 
  getStaffDetailsAction, 
  createScheduleAction, 
  getSchedulesAction 
} from "../../Action/modernAdminActions";

const EditStaffDuties = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Time slots state initialized with empty time fields
  const [timeSlots, setTimeSlots] = useState({
    Sunday: { selected: false, timeSlots: [{ from: "", to: "" }] },
    Monday: { selected: false, timeSlots: [{ from: "", to: "" }] },
    Tuesday: { selected: false, timeSlots: [{ from: "", to: "" }] },
    Wednesday: { selected: false, timeSlots: [{ from: "", to: "" }] },
    Thursday: { selected: false, timeSlots: [{ from: "", to: "" }] },
    Friday: { selected: false, timeSlots: [{ from: "", to: "" }] },
    Saturday: { selected: false, timeSlots: [{ from: "", to: "" }] },
  });

  const [staffDetails, setStaffDetails] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const daysOfWeek = Object.keys(timeSlots);

  // Handle checkbox change for selecting the days
  const handleCheckboxChange = (day) => {
    setTimeSlots((prevState) => {
      const isSelected = !prevState[day].selected;
      return {
        ...prevState,
        [day]: {
          ...prevState[day],
          selected: isSelected,
        },
      };
    });
  };

  // Handle time change for the slots
  const handleTimeChange = (day, index, timeType, value) => {
    setTimeSlots((prevTimeSlots) => {
      const updatedTimeSlots = prevTimeSlots[day].timeSlots.map((slot, idx) => {
        if (idx === index) {
          return {
            ...slot,
            [timeType]: value,
          };
        }
        return slot;
      });
      return {
        ...prevTimeSlots,
        [day]: {
          ...prevTimeSlots[day],
          timeSlots: updatedTimeSlots,
        },
      };
    });
  };

  // Add a time slot
  const handleAddTimeSlot = (day) => {
    setTimeSlots((prevTimeSlots) => {
      return {
        ...prevTimeSlots,
        [day]: {
          ...prevTimeSlots[day],
          timeSlots: [
            ...prevTimeSlots[day].timeSlots,
            { from: "", to: "" },  // New slot with empty fields
          ],
        },
      };
    });
  };

  // Remove a time slot
  const handleRemoveTimeSlot = (day, index) => {
    setTimeSlots((prevTimeSlots) => {
      const updatedTimeSlots = [...prevTimeSlots[day].timeSlots];
      updatedTimeSlots.splice(index, 1);
      return {
        ...prevTimeSlots,
        [day]: {
          ...prevTimeSlots[day],
          timeSlots: updatedTimeSlots,
        },
      };
    });
  };

  // Validate time slots before submitting
  const validateTimeSlots = () => {
    for (const day in timeSlots) {
      const { selected, timeSlots: dayTimeSlots } = timeSlots[day];
      if (selected) {
        for (let i = 0; i < dayTimeSlots.length; i++) {
          const slot = dayTimeSlots[i];
          if (!slot.from || !slot.to) {
            toast.error(`${day}: Both "From Time" and "To Time" are required for time slot ${i + 1}.`);
            return false;
          }
        }
      }
    }
    return true;
  };



  const handleSubmit = async (values) => {
    if (!validateTimeSlots()) return;

    // Check if still loading
    if (isLoading) {
      toast.error("Please wait while staff information is being loaded.");
      return;
    }

    // Check if staff details failed to load
    if (!staffDetails) {
      toast.error("Staff information could not be loaded. Please refresh the page.");
      return;
    }

    // Validate staff exists
    if (!staffDetails.id) {
      toast.error("Staff information is missing. Please refresh the page.");
      return;
    }

    setIsSubmitting(true);
    try {

      const schedulePromises = [];
      let totalSchedules = 0;

      // Create schedules for selected days
      for (const day of daysOfWeek) {
        if (!timeSlots[day].selected) continue;

        const validTimeSlots = timeSlots[day].timeSlots.filter(slot => slot.from && slot.to);
        
        if (validTimeSlots.length === 0) continue;

        // For each valid time slot, create schedule for the next 4 weeks
        for (const slot of validTimeSlots) {
          for (let week = 0; week < 4; week++) {
            const scheduleDate = getNextDateForDay(day, week);
            
            const scheduleData = {
              employee_id: staffDetails.id, // Using staff user ID as employee_id
              schedule_date: scheduleDate,
              start_time: `${scheduleDate}T${slot.from}:00`,
              end_time: `${scheduleDate}T${slot.to}:00`,
              shift_type: getShiftType(slot.from, slot.to, day), // Intelligent shift type determination
              department_id: staffDetails.department_id || staffDetails.department || "default",
              location: "Main Facility",
              notes: `${day} shift - Week ${week + 1}`,
              break_duration_minutes: 30
            };

            schedulePromises.push(createScheduleAction(scheduleData));
            totalSchedules++;
          }
        }
      }

      if (schedulePromises.length === 0) {
        toast.error("Please select at least one day with time slots");
        return;
      }

      // Execute all schedule creations
      toast.success(`Creating ${totalSchedules} schedule records...`);
      
      const results = await Promise.allSettled(schedulePromises);

      // Count successes and failures
      const successes = results.filter(result => 
        result.status === 'fulfilled' && result.value?.success
      );
      const failures = results.filter(result => 
        result.status === 'rejected' || !result.value?.success
      );

      if (successes.length > 0) {
        toast.success(`Successfully created ${successes.length} of ${totalSchedules} schedules`);
        
        if (failures.length > 0) {
          console.warn('Failed schedules:', failures);
          toast.warning(`${failures.length} schedules could not be created`);
        }

        setTimeout(() => {
          navigate(`${config.baseUrl}duty-schedule`);
        }, 2000);
      } else {
        toast.error("Failed to create any schedules. Please check the data and try again.");
        
        // Log first failure for debugging
        if (failures.length > 0 && failures[0].reason) {
          console.error('Schedule creation failed:', failures[0].reason);
        }
      }

    } catch (error) {
      console.error('Schedule submission error:', error);
      const status = error?.response?.status;
      if (status === 403) {
        toast.error(error?.response?.data?.detail || 'You do not have permission to create schedules.');
        return;
      }
      toast.error("An unexpected error occurred while creating schedules");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get next date for a specific day of week
  const getNextDateForDay = (dayName, weeksAhead = 0) => {
    const dayMap = {
      'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
      'Thursday': 4, 'Friday': 5, 'Saturday': 6
    };
    
    const today = new Date();
    const targetDayIndex = dayMap[dayName];
    const currentDayIndex = today.getDay();
    
    // Calculate days until target day
    let daysUntilTarget = targetDayIndex - currentDayIndex;
    if (daysUntilTarget <= 0) {
      daysUntilTarget += 7; // Next week
    }
    
    // Add weeks ahead
    daysUntilTarget += (weeksAhead * 7);
    
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysUntilTarget);
    
    // Return in YYYY-MM-DD format
    return targetDate.toISOString().split('T')[0];
  };

  // Helper function to determine shift type based on time
  const getShiftType = (startTime, endTime, dayName) => {
    // Extract hour from start time (format: "HH:MM")
    const startHour = parseInt(startTime.split(':')[0]);
    
    // Weekend shifts
    if (dayName === 'Saturday' || dayName === 'Sunday') {
      return 'weekend';
    }
    
    // Night shift (6 PM - 6 AM)
    if (startHour >= 18 || startHour < 6) {
      return 'night';
    }
    
    // Evening shift (2 PM - 10 PM)
    if (startHour >= 14 && startHour < 22) {
      return 'evening';
    }
    
    // Day shift (6 AM - 2 PM) - default
    return 'day';
  };

  // Fetch staff details and existing schedules
  useEffect(() => {
    // Validate ID parameter
    if (!id) {
      console.error('No staff ID provided in URL');
      toast.error("Invalid staff ID. Please check the URL.");
      setStaffDetails(null);
      setIsLoading(false);
      return;
    }

    const getStaffDetailsAPI = async () => {
      setIsLoading(true);
      console.log('Loading staff details for ID:', id);
      try {
        // Get staff details from User model
        let res = await getStaffDetailsAction(id);
        console.log('Staff details API response:', res);
        console.log('Response success:', res.success);
        console.log('Response data:', res.data);
        console.log('Response message:', res.message);
        
        if (res.success) {
          setStaffDetails(res.data);
          console.log('Staff details set:', res.data);
          console.log('Staff ID:', res.data.id);
          console.log('Staff name:', res.data.full_name || `${res.data.first_name || ''} ${res.data.last_name || ''}`.trim());

          // Load existing schedules for this staff member
          await loadExistingSchedules(res.data.id);
        } else {
          console.error('Staff details API failed:', res.message);
          toast.error(res.message || "Failed to load staff details");
          setStaffDetails(null);
        }
      } catch (error) {
        console.error('Error loading staff details:', error);
        toast.error("An error occurred while loading staff details");
        setStaffDetails(null);
      } finally {
        setIsLoading(false);
      }
    };

    const loadExistingSchedules = async (staffId) => {
      try {
        const scheduleRes = await getSchedulesAction({
          employee_id: staffId, // Using staff ID as employee_id parameter
          // Get schedules for the next 4 weeks
          start_date: new Date().toISOString().split('T')[0],
          end_date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        });

        if (scheduleRes.success && scheduleRes.data && scheduleRes.data.length > 0) {
          setSchedules(scheduleRes.data);
          const preselectedTimeSlots = mapSchedulesToTimeSlots(scheduleRes.data);
          setTimeSlots(preselectedTimeSlots);
        } else {
          // No existing schedules - keep default empty time slots
          console.log('No existing schedules found for staff:', staffId);
        }
      } catch (error) {
        console.warn('Could not load existing schedules:', error);
        // Don't show error to user as this is not critical
      }
    };

    getStaffDetailsAPI();
  }, [id]);

  // Map schedule data from API to the timeSlots state format
  const mapSchedulesToTimeSlots = (schedules) => {
    const updatedTimeSlots = {};

    // Initialize all days with empty time slots
    daysOfWeek.forEach((day) => {
      updatedTimeSlots[day] = {
        selected: false,
        timeSlots: [{ from: "", to: "" }],
      };
    });

    // Group schedules by day of week
    const schedulesByDay = {};
    
    schedules.forEach((schedule) => {
      try {
        // Extract date and time from schedule
        const scheduleDate = new Date(schedule.schedule_date);
        const dayIndex = scheduleDate.getDay();
        const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayIndex];

        if (!schedulesByDay[dayName]) {
          schedulesByDay[dayName] = [];
        }

        // Extract time from datetime strings
        const startTime = new Date(schedule.start_time).toTimeString().substring(0, 5);
        const endTime = new Date(schedule.end_time).toTimeString().substring(0, 5);

        // Only add if we have valid times
        if (startTime !== 'Invalid' && endTime !== 'Invalid') {
          schedulesByDay[dayName].push({
            from: startTime,
            to: endTime
          });
        }
      } catch (error) {
        console.warn('Could not parse schedule:', schedule, error);
      }
    });

    // Apply existing schedules to time slots
    Object.keys(schedulesByDay).forEach((dayName) => {
      if (updatedTimeSlots[dayName] && schedulesByDay[dayName].length > 0) {
        updatedTimeSlots[dayName].selected = true;
        updatedTimeSlots[dayName].timeSlots = schedulesByDay[dayName];
      }
    });

    return updatedTimeSlots;
  };

  return (
    <>
      <Dashboardheader />
      <div className="wrapper">
        <Sidebar />
        <div id="content" className="healthcare-container">
          <Container fluid className="p-4">
            {/* Header Section */}
            <Row className="mb-4">
              <Col lg={12}>
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '20px'
                }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <FaCalendarAlt size={24} style={{ color: '#4b5563' }} />
                      <div>
                        <h4 className="mb-1" style={{ color: '#1f2937', fontWeight: '600' }}>
                          Edit Staff Duties
                        </h4>
                        <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                          Configure weekly schedule and duty assignments
                        </div>
                      </div>
                    </div>
                    <Link 
                      to={`${config.baseUrl}duty-schedule`}
                      style={{
                        backgroundColor: '#f8fafc',
                        color: '#4b5563',
                        border: '1px solid #d1d5db',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <BsArrowLeftCircleFill size={16} />
                      Back to Schedule
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>
            
            {isLoading ? (
              <Row>
                <Col lg={12}>
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '40px',
                    textAlign: 'center'
                  }}>
                    <Spinner animation="border" role="status" className="mb-3" style={{ color: '#2563eb' }}>
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <h6 style={{ color: '#374151', marginBottom: '8px' }}>Loading Staff Information</h6>
                    <p style={{ color: '#6b7280', margin: '0', fontSize: '0.875rem' }}>
                      Please wait while we retrieve the staff details...
                    </p>
                  </div>
                </Col>
              </Row>
            ) : !staffDetails ? (
              <Row>
                <Col lg={12}>
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    padding: '40px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                    <h6 style={{ color: '#dc2626', marginBottom: '8px' }}>Failed to Load Staff Information</h6>
                    <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '0.875rem' }}>
                      Unable to retrieve staff details. Please check the URL and try again.
                    </p>
                    <Link 
                      to={`${config.baseUrl}duty-schedule`} 
                      style={{
                        backgroundColor: '#2563eb',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      Back to Duty Schedule
                    </Link>
                  </div>
                </Col>
              </Row>
            ) : (
              <>
                {/* Staff Information Card */}
                <Row className="mb-4">
                  <Col lg={12}>
                    <div style={{
                      backgroundColor: '#f0fdf4',
                      border: '1px solid #d1fae5',
                      borderRadius: '8px',
                      padding: '20px'
                    }}>
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div style={{
                          backgroundColor: '#16a34a',
                          color: 'white',
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <FaUser size={18} />
                        </div>
                        <div>
                          <h6 style={{ color: '#166534', margin: '0', fontWeight: '600' }}>Staff Information</h6>
                          <div style={{ color: '#15803d', fontSize: '0.875rem' }}>
                            Current staff member details
                          </div>
                        </div>
                      </div>
                      <Row>
                        <Col md={4}>
                          <div style={{ marginBottom: '8px' }}>
                            <strong style={{ color: '#374151' }}>Name:</strong>{' '}
                            <span style={{ color: '#1f2937' }}>
                              {staffDetails.full_name || `${staffDetails.first_name || ''} ${staffDetails.last_name || ''}`.trim()}
                            </span>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div style={{ marginBottom: '8px' }}>
                            <strong style={{ color: '#374151' }}>Email:</strong>{' '}
                            <span style={{ color: '#1f2937' }}>{staffDetails.email}</span>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div style={{ marginBottom: '8px' }}>
                            <strong style={{ color: '#374151' }}>Department:</strong>{' '}
                            <span style={{ color: '#1f2937' }}>{staffDetails.department || 'Not assigned'}</span>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>

                {/* Schedule Configuration */}
                <Row>
                  <Col lg={12}>
                    <div style={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '20px'
                    }}>
                      <div className="d-flex align-items-center gap-3 mb-4">
                        <MdSchedule size={24} style={{ color: '#2563eb' }} />
                        <div>
                          <h5 style={{ color: '#1f2937', margin: '0', fontWeight: '600' }}>Weekly Schedule Configuration</h5>
                          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            Select days and configure time slots for staff duties
                          </div>
                        </div>
                      </div>

                      <Formik
                        initialValues={staffDetails}
                        enableReinitialize
                        onSubmit={handleSubmit}
                      >
                        {({ handleSubmit }) => (
                          <Form onSubmit={handleSubmit}>
                            <div className="schedule-grid">
                              {daysOfWeek.map((day) => (
                                <Card key={day} className="mb-3" style={{ border: timeSlots[day].selected ? '2px solid #2563eb' : '1px solid #e2e8f0' }}>
                                  <Card.Header style={{
                                    backgroundColor: timeSlots[day].selected ? '#eff6ff' : '#f8fafc',
                                    border: 'none',
                                    padding: '16px 20px'
                                  }}>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div className="d-flex align-items-center gap-3">
                                        <Form.Check
                                          type="checkbox"
                                          checked={timeSlots[day].selected}
                                          onChange={() => handleCheckboxChange(day)}
                                          style={{ transform: 'scale(1.2)' }}
                                        />
                                        <div>
                                          <h6 style={{ 
                                            color: timeSlots[day].selected ? '#1e40af' : '#374151', 
                                            margin: '0', 
                                            fontWeight: '600' 
                                          }}>
                                            {day}
                                          </h6>
                                          <div style={{ 
                                            color: timeSlots[day].selected ? '#3b82f6' : '#6b7280', 
                                            fontSize: '0.75rem' 
                                          }}>
                                            {timeSlots[day].selected ? 
                                              `${timeSlots[day].timeSlots.length} time slot(s)` : 
                                              'Not scheduled'
                                            }
                                          </div>
                                        </div>
                                      </div>
                                      {timeSlots[day].selected && (
                                        <Badge 
                                          bg="primary" 
                                          style={{ 
                                            backgroundColor: '#2563eb',
                                            fontSize: '0.7rem',
                                            padding: '4px 8px'
                                          }}
                                        >
                                          Active
                                        </Badge>
                                      )}
                                    </div>
                                  </Card.Header>
                                  
                                  {timeSlots[day].selected && (
                                    <Card.Body style={{ padding: '20px' }}>
                                      {timeSlots[day].timeSlots.map((slot, index) => (
                                        <div key={index} className="mb-3">
                                          <div style={{
                                            backgroundColor: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            padding: '16px'
                                          }}>
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                              <h6 style={{ color: '#374151', margin: '0', fontSize: '0.875rem', fontWeight: '600' }}>
                                                <MdAccessTime size={16} className="me-2" />
                                                Time Slot {index + 1}
                                              </h6>
                                              {timeSlots[day].timeSlots.length > 1 && (
                                                <button
                                                  type="button"
                                                  onClick={() => handleRemoveTimeSlot(day, index)}
                                                  style={{
                                                    backgroundColor: '#fef2f2',
                                                    color: '#dc2626',
                                                    border: '1px solid #fecaca',
                                                    borderRadius: '4px',
                                                    padding: '4px 8px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                  }}
                                                >
                                                  <FaTrash size={12} />
                                                  Remove
                                                </button>
                                              )}
                                            </div>
                                            <Row>
                                              <Col md={6}>
                                                <Form.Group className="mb-2">
                                                  <Form.Label style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '500' }}>
                                                    <FaClock size={12} className="me-1" />
                                                    Start Time
                                                  </Form.Label>
                                                  <Form.Control
                                                    type="time"
                                                    value={slot.from || ""}
                                                    onChange={(e) => handleTimeChange(day, index, "from", e.target.value)}
                                                    style={{
                                                      border: '1px solid #d1d5db',
                                                      borderRadius: '6px',
                                                      fontSize: '0.875rem'
                                                    }}
                                                  />
                                                </Form.Group>
                                              </Col>
                                              <Col md={6}>
                                                <Form.Group className="mb-2">
                                                  <Form.Label style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '500' }}>
                                                    <FaClock size={12} className="me-1" />
                                                    End Time
                                                  </Form.Label>
                                                  <Form.Control
                                                    type="time"
                                                    value={slot.to || ""}
                                                    onChange={(e) => handleTimeChange(day, index, "to", e.target.value)}
                                                    style={{
                                                      border: '1px solid #d1d5db',
                                                      borderRadius: '6px',
                                                      fontSize: '0.875rem'
                                                    }}
                                                  />
                                                </Form.Group>
                                              </Col>
                                            </Row>
                                          </div>
                                        </div>
                                      ))}
                                      
                                      <button
                                        type="button"
                                        onClick={() => handleAddTimeSlot(day)}
                                        style={{
                                          backgroundColor: '#eff6ff',
                                          color: '#2563eb',
                                          border: '1px solid #bfdbfe',
                                          borderRadius: '6px',
                                          padding: '8px 16px',
                                          fontSize: '0.875rem',
                                          fontWeight: '500',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                          width: '100%',
                                          justifyContent: 'center'
                                        }}
                                      >
                                        <FaPlus size={12} />
                                        Add Another Time Slot
                                      </button>
                                    </Card.Body>
                                  )}
                                </Card>
                              ))}
                            </div>

                            {/* Submit Button */}
                            <div style={{ 
                              borderTop: '1px solid #e2e8f0', 
                              paddingTop: '20px', 
                              marginTop: '20px',
                              textAlign: 'center'
                            }}>
                              <button 
                                type="submit" 
                                disabled={isSubmitting}
                                style={{
                                  backgroundColor: isSubmitting ? '#9ca3af' : '#2563eb',
                                  color: 'white',
                                  border: 'none',
                                  padding: '12px 32px',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  margin: '0 auto',
                                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {isSubmitting ? (
                                  <>
                                    <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                    Creating Schedules...
                                  </>
                                ) : (
                                  <>
                                    <MdSave size={16} />
                                    Save Schedule
                                  </>
                                )}
                              </button>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </Container>
        </div>
      </div>
    </>
  );
};

export default EditStaffDuties;
